// API utilities per la gestione dei mazzi Magic
const API_BASE = '/api/decks';

/**
 * Gestisce le risposte delle API
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Errore HTTP: ${response.status}`);
  }
  return response.json();
};

/**
 * Ottiene tutti i mazzi disponibili (solo quelli pubblici)
 */
export const fetchDecks = async () => {
  try {
    // Filtra solo i mazzi pubblici lato server
    const response = await fetch(
      `${API_BASE}?populate=*&fields=*,documentId&filters[isPublic][$eq]=true`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await handleResponse(response);
    return data.decks || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel caricamento dei mazzi:', error);
    throw error;
  }
};

/**
 * Ottiene i mazzi dell'utente autenticato
 */
export const fetchUserDecks = async () => {
  try {
    // Includi tutti i campi necessari incluso documentId
    const response = await fetch(
      `${API_BASE}/user?populate=*&fields=*,documentId`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await handleResponse(response);
    return data.decks || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel caricamento dei mazzi utente:', error);
    throw error;
  }
};

/**
 * Ottiene un mazzo specifico per ID
 */
export const fetchDeckById = async (deckId) => {
  try {
    // Includi tutti i campi necessari incluso documentId
    const response = await fetch(
      `${API_BASE}/${deckId}?populate=*&fields=*,documentId`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    const data = await handleResponse(response);
    return data.deck;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel caricamento del mazzo:', error);
    throw error;
  }
};

/**
 * Ottiene più mazzi per ID multipli
 */
export const fetchDecksByIds = async (deckIds) => {
  try {
    if (!deckIds || deckIds.length === 0) {
      return [];
    }

    // Se c'è solo un ID, usa $eq
    if (deckIds.length === 1) {
      const fullUrl = `${API_BASE}?populate=*&fields=*,documentId&filters[documentId][$eq]=${deckIds[0]}&filters[isPublic][$eq]=true`;

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await handleResponse(response);

      // Filtra lato client se il backend non filtra correttamente
      const filteredDecks = (data.decks || []).filter((deck) =>
        deckIds.includes(deck.documentId)
      );
      return filteredDecks;
    }

    // Per più ID, usa $in con sintassi array
    const idsQuery = deckIds
      .map((id, index) => `filters[documentId][$in][${index}]=${id}`)
      .join('&');
    const fullUrl = `${API_BASE}?populate=*&fields=*,documentId&${idsQuery}&filters[isPublic][$eq]=true`;

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await handleResponse(response);

    // Filtra lato client se il backend non filtra correttamente
    const filteredDecks = (data.decks || []).filter((deck) =>
      deckIds.includes(deck.documentId)
    );
    return filteredDecks;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel caricamento dei mazzi per ID multipli:', error);
    throw error;
  }
};

/**
 * Crea un nuovo mazzo
 */
export const createDeck = async (deckData) => {
  try {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(deckData),
    });

    const data = await handleResponse(response);
    return data.deck;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella creazione del mazzo:', error);
    throw error;
  }
};

/**
 * Aggiorna un mazzo esistente
 */
export const updateDeck = async (deckId, deckData) => {
  try {
    const response = await fetch(`${API_BASE}/${deckId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(deckData),
    });

    const data = await handleResponse(response);
    return data.deck;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'aggiornamento del mazzo:", error);
    throw error;
  }
};

/**
 * Elimina un mazzo
 */
export const deleteDeck = async (deckId) => {
  try {
    const response = await fetch(`${API_BASE}/${deckId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    await handleResponse(response);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'eliminazione del mazzo:", error);
    throw error;
  }
};

/**
 * Cerca mazzi con filtri
 */
export const searchDecks = async (searchParams) => {
  try {
    const queryString = new URLSearchParams(searchParams).toString();
    const response = await fetch(`${API_BASE}/search?${queryString}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await handleResponse(response);
    return data.decks || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella ricerca dei mazzi:', error);
    throw error;
  }
};

/**
 * Analizza una lista di carte e restituisce statistiche e dati completi
 */
export const analyzeDeckList = async (deckList) => {
  try {
    const response = await fetch(`${API_BASE}/analyze`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ deckList }),
    });

    const data = await handleResponse(response);
    return data; // Restituisce tutto: { analysis, cards, deckList }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'analisi del mazzo:", error);
    throw error;
  }
};

/**
 * Ottiene i mazzi preferiti dell'utente
 */
export const fetchFavoriteDecks = async () => {
  try {
    const response = await fetch(`${API_BASE}/favorites`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await handleResponse(response);
    return data.decks || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel caricamento dei mazzi preferiti:', error);
    throw error;
  }
};

/**
 * Aggiunge/rimuove un mazzo dai preferiti
 */
export const toggleFavoriteDeck = async (deckId) => {
  try {
    const response = await fetch(`${API_BASE}/${deckId}/favorite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await handleResponse(response);
    return data.isFavorite;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella gestione dei preferiti:', error);
    throw error;
  }
};

/**
 * Ottiene statistiche aggregate sui mazzi
 */
export const fetchDeckStats = async () => {
  try {
    const response = await fetch(`${API_BASE}/stats`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    const data = await handleResponse(response);
    return data.stats;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel caricamento delle statistiche:', error);
    throw error;
  }
};
